/* eslint-disable react/jsx-key */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import setPageMetadata from "@insite/client-framework/Common/Utilities/setPageMetadata";
import { trackPageChange } from "@insite/client-framework/Common/Utilities/tracking";
import Zone from "@insite/client-framework/Components/Zone";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setBreadcrumbs from "@insite/client-framework/Store/Components/Breadcrumbs/Handlers/SetBreadcrumbs";
import {
    getSelectedCategoryPath,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import CurrentCategory from "@insite/content-library/Components/CurrentCategory";
import Page from "@insite/mobius/Page";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CategoryProductTypeTile from "@ucs/Components/Category/CategoryProductTypeTile";
import ProductMenu, { ProductLinks } from "@ucs/Components/Category/ProductsMenu";
import { CarouselStyles } from "@ucs/Components/EmblaCarousel/EmblaSlideShow";
import EmblaSlideShow from "@ucs/Components/EmblaCarousel/EmblaSlideShow";
import { CategoriesLvl1, getChildCategories, getPrductMenuLinks, isCategoryLvl1 } from "@ucs/Helpers/CategoryHelpers";
import { parseHtmlGetBody } from "@ucs/Helpers/Parsers";
import isEmpty from "lodash/isEmpty";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface State {
    metadataSetForId?: string;
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
    const categoriesList = getCategoriesDataView(state);

    return {
        catalogPageState,
        category: getCategoryState(
            state,
            catalogPageState.value?.categoryIdWithBrandId ?? catalogPageState.value?.categoryId,
        ).value,
        location,
        websiteName: state.context.website.name,
        websiteSettings: getSettingsCollection(state).websiteSettings,
        categoriesList,
        breadcrumbLinks: state.components.breadcrumbs.links,
    };
};

const mapDispatchToProps = {
    loadCategories,
    setBreadcrumbs,
};

export interface CategoryDetailsPageStyles {
    typeWrapper?: InjectableCss;
    applicationWrapper?: InjectableCss;
    middleContentWrapper?: InjectableCss;
    bottomContentWrapper?: InjectableCss;
    slideshowStyles?: CarouselStyles;
}

export const categoryDetailsStyles: CategoryDetailsPageStyles = {
    typeWrapper: {
        css: css`
            width: fit-content;
            margin: 0 auto;
            padding: 48px 4px 90px;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 32px 2px 35px;
                    `,
                    css`
                        padding: 32px 4px 35px;
                    `,
                    css`
                        padding: 40px 6px 50px;
                    `,
                    css``,
                    css``,
                ])};
            & div {
                display: grid;
                grid-template-rows: auto;
                justify-items: center;
                ${({ theme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            grid-gap: 10px;
                            grid-template-columns: repeat(2, calc(315px * 0.52));
                        `,
                        css`
                            grid-gap: 10px;
                            grid-template-columns: repeat(2, calc(315px * 0.63));
                        `,
                        css`
                            grid-gap: 20px;
                            grid-template-columns: repeat(3, calc(315px * 0.75));
                        `,
                        css`
                            grid-gap: 30px;
                            grid-template-columns: repeat(3, calc(315px * 0.85));
                        `,
                        css``,
                    ])};

                @media only screen and (min-width: 1200px) and (max-width: 1445px) {
                    grid-gap: 30px;
                    grid-template-columns: repeat(3, 315px);
                }
                @media (min-width: 1446px) {
                    grid-gap: 30px;
                    grid-template-columns: repeat(4, 315px);
                }
            }
        `,
    },
    applicationWrapper: {
        css: css`
            margin-top: 48px;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 32px;
                    `,
                    css`
                        margin-top: 32px;
                    `,
                    css``,
                    css``,
                    css``,
                ])};
        `,
    },
    middleContentWrapper: {
        css: css`
            width: 100%;
            padding-bottom: 100px;
            margin: 0 auto;
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 0 30px;

            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 1fr;
                        gap: 40px 0;
                        padding: 37px calc((100% - 335px) / 2) 56px;
                    `,
                    css`
                        grid-template-columns: 1fr;
                        gap: 40px 0;
                        padding: 37px 40px 56px;
                    `,
                    css`
                        max-width: 748px;
                    `,
                    css`
                        max-width: 863px;
                    `,
                    css``,
                ])};

            @media only screen and (min-width: 1200px) and (max-width: 1445px) {
                max-width: 1005px;
            }
            @media (min-width: 1446px) {
                max-width: 1350px;
            }
        `,
    },
    slideshowStyles: {
        wrapper: {
            css: css`
                position: relative;
                width: 100%;
                max-height: 720px;
            `,
        },
        imageStyles: {
            css: css`
                height: 50vw;
                max-height: 720px;
                background-size: cover;
            `,
        },
    },
};

const hasBottomContentImages = (cat: Readonly<Category>) => {
    return (
        !isEmpty(cat.properties.bottomContentAreaImage1) ||
        !isEmpty(cat.properties.bottomContentAreaImage2) ||
        !isEmpty(cat.properties.bottomContentAreaImage3) ||
        !isEmpty(cat.properties.bottomContentAreaImage4)
    );
};

const getImageLinks = (cat: Readonly<Category>) => {
    return [
        { url: cat.properties.bottomContentAreaImage1 },
        { url: cat.properties.bottomContentAreaImage2 },
        { url: cat.properties.bottomContentAreaImage3 },
        { url: cat.properties.bottomContentAreaImage4 },
    ];
};

const styles = categoryDetailsStyles;

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

class CategoryDetailsPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {};
    }

    UNSAFE_componentWillMount() {
        this.setMetadata();
        const { categoriesList, loadCategories } = this.props;
        if (!categoriesList.isLoading && !categoriesList.value) {
            loadCategories();
        }
        if (this.props.catalogPageState.value) {
            if (
                this.props.breadcrumbLinks?.map(o => o.children?.toString()).join(",") !==
                this.props.catalogPageState.value.breadCrumbs?.map(o => o.text).join(",")
            ) {
                this.props.setBreadcrumbs({
                    links: this.props.catalogPageState.value!.breadCrumbs!.map(o => ({
                        children: o.text,
                        href: o.url,
                    })),
                });
            }
        }
    }

    componentDidUpdate() {
        const { category } = this.props;
        if (category && category.id !== this.state.metadataSetForId) {
            trackPageChange();
            this.setMetadata();
        }
    }

    setMetadata() {
        const { catalogPageState, category, websiteName, location, websiteSettings } = this.props;
        if (!catalogPageState?.value || !category) {
            return;
        }

        const { metaDescription, metaKeywords, openGraphImage, openGraphTitle, openGraphUrl, title } =
            catalogPageState.value;

        setPageMetadata(
            {
                metaDescription,
                metaKeywords,
                openGraphImage,
                openGraphTitle,
                openGraphUrl,
                currentPath: location.pathname,
                canonicalPath: catalogPageState.value.canonicalPath,
                alternateLanguageUrls: catalogPageState.value.alternateLanguageUrls ?? undefined,
                title: title || category.name,
                websiteName,
            },
            websiteSettings,
        );

        this.setState({
            metadataSetForId: category.id,
        });
    }

    render() {
        const { categoriesList, category, id } = this.props;
        const catLinks: ProductLinks[] =
            categoriesList.value && category ? getPrductMenuLinks(categoriesList.value, category.name) : [];

        const subCategoryIds = category?.subCategoryIds?.map(id => (id.includes(":") ? id.substr(0, id.indexOf(":")) : id)) || [];
        const childCategories =
            categoriesList.value && category ? getChildCategories(categoriesList.value, subCategoryIds) : null;
        const isLvlOne = category ? isCategoryLvl1(category.path) : null;

        console.log(childCategories);

        return (
            <Page>
                <CurrentCategory>
                    <Zone contentId={this.props.id} zoneName="Banner" />

                    {isLvlOne && (
                        <StyledWrapper style={{ paddingTop: 30 }}>
                            <ProductMenu links={catLinks}></ProductMenu>
                        </StyledWrapper>
                    )}

                    {category && (category.name === CategoriesLvl1.TYPE || !isLvlOne) && (
                        <StyledWrapper {...styles.typeWrapper}>
                            <div id="typeWrapper">
                                {childCategories &&
                                    childCategories.map(cat => {
                                        return (
                                            <CategoryProductTypeTile
                                                key={cat.id}
                                                link={cat.path}
                                                name={cat.name}
                                                image={cat.smallImagePath}
                                                width={315}
                                                height={315}
                                            ></CategoryProductTypeTile>
                                        );
                                    })}
                            </div>
                        </StyledWrapper>
                    )}

                    {category && category.name === CategoriesLvl1.APPLICATION && (
                        <StyledWrapper {...styles.applicationWrapper}>
                            <Zone zoneName="ApplicationTiles" contentId={id} />
                        </StyledWrapper>
                    )}

                    {category && !isLvlOne && (
                        <>
                            <StyledWrapper {...styles.middleContentWrapper}>
                                {category.properties.leftContentArea && (
                                    <div>{parseHtmlGetBody(category.properties.leftContentArea)}</div>
                                )}
                                {category.properties.rightContentArea && (
                                    <div>{parseHtmlGetBody(category.properties.rightContentArea)}</div>
                                )}
                            </StyledWrapper>
                            {hasBottomContentImages(category) && (
                                <StyledWrapper>
                                    <EmblaSlideShow
                                        id="c2slide"
                                        images={getImageLinks(category)}
                                        extendedStyles={styles.slideshowStyles}
                                    ></EmblaSlideShow>
                                </StyledWrapper>
                            )}
                        </>
                    )}

                    <Zone contentId={id} zoneName="Content" />
                </CurrentCategory>
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CategoryDetailsPage),
    definition: {
        hasEditableUrlSegment: false,
        hasEditableTitle: true,
        supportsCategorySelection: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "CategoryDetailsPage" instead of this constant.
 */
export const CategoryDetailsPageContext = "CategoryDetailsPage";